/**
 * StatusTag — the small state pill used for attendance day statuses, model state, and
 * anything else with a handful of fixed states.
 *
 * WHY INLINE STYLES: the tint is built with `color-mix()` on the theme CSS vars rather
 * than Tailwind's `bg-success/15`. Tailwind v3 silently DROPS the opacity modifier when a
 * colour is defined as a bare `var(--x)` (as this project's status tokens are) — the
 * utility compiles to nothing at all, which is how these badges ended up with coloured
 * text on no background. Referencing the vars directly can't be dropped. Same approach as
 * the Timeline tab's in/out dots.
 */
import type { ReactNode } from 'react';

/** Semantic tones → the theme CSS var that colours the tag. */
const TONE_VAR = {
  success: 'var(--success)',
  warning: 'var(--warning)',
  info: 'var(--info)',
  danger: 'var(--destructive)',
  brand: 'var(--brand)',
  neutral: 'var(--muted-foreground)',
} as const;

export type StatusTone = keyof typeof TONE_VAR;

/**
 * The raw CSS var behind a tone, for the places that need the colour WITHOUT the pill —
 * the alert list's severity stripe down the left edge of a row, for instance.
 */
export function toneVar(tone: StatusTone) {
  return TONE_VAR[tone];
}

/** `color` at `pct`% over the page — a tint that works in both light and dark themes. */
const mix = (color: string, pct: number) => `color-mix(in srgb, ${color} ${pct}%, transparent)`;

export interface StatusTagProps {
  children: ReactNode;
  tone?: StatusTone;
  /**
   * `soft` (default) is a tinted pill — the right weight for a state you read.
   * `solid` is a filled pill that shouts; use it for the one field that should win the
   * row's attention (alert severity), so status and severity don't compete.
   */
  variant?: 'soft' | 'solid';
  /** Leading state dot. On by default for `soft`; off for `solid`, where the fill carries it. */
  dot?: boolean;
  /** Native tooltip, for when the label deliberately summarises a finer-grained state. */
  title?: string;
  className?: string;
}

export default function StatusTag({
  children,
  tone = 'neutral',
  variant = 'soft',
  dot,
  title,
  className = '',
}: StatusTagProps) {
  const c = TONE_VAR[tone];
  const solid = variant === 'solid';
  const showDot = dot ?? !solid;
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wide ${solid ? '' : 'border'} ${className}`}
      style={
        solid
          // White-on-colour: every tone var in this palette is a mid-weight hue, so plain
          // white text clears contrast in both themes.
          ? { backgroundColor: c, color: '#fff' }
          : { backgroundColor: mix(c, 14), borderColor: mix(c, 32), color: c }
      }
    >
      {showDot && (
        <span
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{ backgroundColor: solid ? '#fff' : c }}
        />
      )}
      {children}
    </span>
  );
}
